const configuredApiBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") || "";
const configuredAppBaseUrl = import.meta.env.VITE_APP_URL?.replace(/\/$/, "") || "https://orizonte.ink";

export const API_BASE_URL = configuredApiBaseUrl;

export class BackendError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly payload?: unknown,
  ) {
    super(message);
    this.name = "BackendError";
  }
}

type BackendTokenProvider = () => Promise<string | null>;
type BackendCacheScopeProvider = () => Promise<string | null> | string | null;

type CachedPayload = {
  savedAt: number;
  payload: unknown;
};

type BackendCacheProvider = {
  read: (key: string) => CachedPayload | null | Promise<CachedPayload | null>;
  write: (key: string, entry: CachedPayload) => void | Promise<void>;
  deleteMatching?: (matcher?: string | RegExp) => void | Promise<void>;
};

type BackendFetchOptions = RequestInit & {
  cacheTtlMs?: number;
  staleTtlMs?: number;
  forceRefresh?: boolean;
  cacheKey?: string;
};

const DEFAULT_CACHE_TTL_MS = 30_000;
const DEFAULT_STALE_TTL_MS = 5 * 60_000;
const CACHE_PREFIX = "orizonte:api-cache:";
const memoryCache = new Map<string, CachedPayload>();
const pendingRequests = new Map<string, Promise<unknown>>();
let getBackendToken: BackendTokenProvider | null = null;
let getBackendCacheScope: BackendCacheScopeProvider | null = null;
let backendCacheProvider: BackendCacheProvider | null = null;

export const configureClerkToken = (fn: () => Promise<string | null>) => {
  getBackendToken = fn;
  getBackendCacheScope = null;
};

export const configureBackendAuth = ({
  getToken,
  getCacheScope,
}: {
  getToken: BackendTokenProvider;
  getCacheScope?: BackendCacheScopeProvider;
}) => {
  getBackendToken = getToken;
  getBackendCacheScope = getCacheScope ?? null;
};

export const configureBackendCache = (provider: BackendCacheProvider | null) => {
  backendCacheProvider = provider;
};

const parseJwtSubject = (token: string | null) => {
  if (!token) return "anonymous";
  try {
    const payload = token.split(".")[1];
    if (!payload) return "authenticated";
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="))) as { sub?: string };
    return decoded.sub || "authenticated";
  } catch {
    return "authenticated";
  }
};

const isPackagedDesktopUrl = () => {
  if (typeof window === "undefined") return false;
  const protocol = window.location.protocol;
  const host = window.location.hostname;
  return protocol === "tauri:" || host.endsWith("tauri.localhost");
};

const apiBaseUrl = () => {
  if (configuredApiBaseUrl) return `${configuredApiBaseUrl}/api`;
  if (isPackagedDesktopUrl()) return `${configuredAppBaseUrl}/api`;
  return "/api";
};

const canUseSessionStorage = () => typeof window !== "undefined" && Boolean(window.sessionStorage);

const readStoredCache = async (key: string): Promise<CachedPayload | null> => {
  const memoryValue = memoryCache.get(key);
  if (memoryValue) return memoryValue;

  if (backendCacheProvider) {
    try {
      const cached = await backendCacheProvider.read(key);
      if (cached) {
        memoryCache.set(key, cached);
        return cached;
      }
    } catch {
      // Fall through to sessionStorage; desktop cache should never break the request path.
    }
  }

  if (!canUseSessionStorage()) return null;

  try {
    const raw = window.sessionStorage.getItem(`${CACHE_PREFIX}${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPayload;
    memoryCache.set(key, parsed);
    return parsed;
  } catch {
    return null;
  }
};

const writeStoredCache = async (key: string, payload: unknown) => {
  const entry = { savedAt: Date.now(), payload };
  memoryCache.set(key, entry);

  if (backendCacheProvider) {
    try {
      await backendCacheProvider.write(key, entry);
    } catch {
      // Ignore persistent cache failures; memory/session cache can still serve this run.
    }
  }

  if (!canUseSessionStorage()) return;

  try {
    window.sessionStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(entry));
  } catch {
    // Storage can be unavailable in private mode or full; memory cache still helps this session.
  }
};

export const invalidateBackendCache = (matcher?: string | RegExp) => {
  const shouldDelete = (key: string) => {
    if (!matcher) return true;
    return typeof matcher === "string" ? key.includes(matcher) : matcher.test(key);
  };

  [...memoryCache.keys()].forEach((key) => {
    if (shouldDelete(key)) memoryCache.delete(key);
  });

  if (!canUseSessionStorage()) return;

  try {
    [...Array(window.sessionStorage.length).keys()]
      .map((index) => window.sessionStorage.key(index))
      .filter((key): key is string => Boolean(key?.startsWith(CACHE_PREFIX)))
      .forEach((storageKey) => {
        const cacheKey = storageKey.slice(CACHE_PREFIX.length);
        if (shouldDelete(cacheKey)) window.sessionStorage.removeItem(storageKey);
      });
  } catch {
    // Ignore storage cleanup errors; the in-memory cache was already cleared.
  }

  if (backendCacheProvider?.deleteMatching) {
    try {
      const result = backendCacheProvider.deleteMatching(matcher);
      if (result instanceof Promise) {
        void result.catch(() => {});
      }
    } catch {
      // Ignore persistent cache cleanup failures.
    }
  }
};

export const backendFetch = async (path: string, options: BackendFetchOptions = {}) => {
  const headers = new Headers(options.headers || {});
  headers.set("Content-Type", "application/json");

  const token = getBackendToken ? await getBackendToken() : null;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  Object.entries(authHeaders).forEach(([key, value]) => headers.set(key, value));

  const method = (options.method || "GET").toUpperCase();
  const isCacheable = method === "GET" && options.cache !== "no-store";
  const providerScope = getBackendCacheScope ? await getBackendCacheScope() : null;
  const cacheScope = providerScope || parseJwtSubject(token);
  const cacheKey = `${cacheScope}:${options.cacheKey || path}`;
  const cacheTtlMs = options.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS;
  const staleTtlMs = Math.max(options.staleTtlMs ?? DEFAULT_STALE_TTL_MS, cacheTtlMs);
  const cached = isCacheable ? await readStoredCache(cacheKey) : null;
  const age = cached ? Date.now() - cached.savedAt : Number.POSITIVE_INFINITY;
  const isFresh = Boolean(cached && age <= cacheTtlMs);
  const canUseStale = Boolean(cached && age <= staleTtlMs);

  if (!options.forceRefresh && cached && isFresh) {
    return cached.payload;
  }

  if (isCacheable && pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  const request = fetch(`${apiBaseUrl()}${path}`, {
    ...options,
    headers,
  }).then(async (response) => {
    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json") ? await response.json() : null;

    if (!response.ok) {
      const message = payload?.error || payload?.message || `Request failed (${response.status})`;
      throw new BackendError(
        typeof message === "string" ? message : "Request failed",
        response.status,
        payload?.code,
        payload,
      );
    }

    if (isCacheable) {
      await writeStoredCache(cacheKey, payload);
    } else if (method !== "GET") {
      invalidateBackendCache(cacheScope);
    }

    return payload;
  }).catch((error) => {
    if (cached && canUseStale) return cached.payload;
    throw error;
  }).finally(() => {
    if (isCacheable) pendingRequests.delete(cacheKey);
  });

  if (isCacheable) pendingRequests.set(cacheKey, request);
  return request;
};
